import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import { createLogger } from './logger';

const log = createLogger('lazy-reload');

// Stale-chunk auto-reload (founder 2026-06-23).
// Sorun: yeni deploy `rsync --delete` ile eski hash'li route chunk'larını siler.
// Açık duran ESKİ sekme yeni bir sayfaya gezinince `lazy()` artık var olmayan
// hash'li dosyayı ister → 404 → "Importing a module script failed" (veya Caddy
// SPA-fallback'i `.js` yerine index.html/text-html dönerse MIME hatası). Refresh
// taze index.html getirip yeni chunk referanslarını yükler → düzelir.
//
// Çözüm: dinamik import patlarsa SADECE bir kez (cooldown'lı) sayfayı otomatik
// yenile → kullanıcı "Sayfayı yenile" ekranını hiç görmez. Cooldown içinde
// tekrar patlarsa (gerçek bug / ağ kopukluğu) reload etme → ErrorBoundary devreye
// girer (sonsuz reload döngüsü engellenir).

const RELOAD_TS_KEY = 'custel:chunk-reload-ts';
// Stale-chunk reload normalde ~1-2 sn'de yeni chunk'ı yükler. Cooldown bundan
// uzun: aynı yükleme cooldown içinde 2. kez patlarsa "gerçek hata" kabul edilir.
// Dakikalar sonraki yeni bir deploy ise cooldown'ı aşar → tekrar reload hakkı.
const RELOAD_COOLDOWN_MS = 10_000;

function isLikelyStaleChunkError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error);
  return (
    /importing a module script failed/i.test(msg) ||
    /failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /'?text\/html'?/i.test(msg) || // MIME mismatch: SPA fallback .js yerine index.html döndü
    /Loading chunk \d+ failed/i.test(msg) ||
    /ChunkLoadError/i.test(msg)
  );
}

/**
 * `React.lazy` muadili — route chunk yüklenemezse bir kez otomatik reload eder.
 * Kullanım `lazy` ile birebir aynı; App.tsx'te `lazy` olarak alias'lanır.
 */
export function lazyWithReload<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() =>
    factory().catch((error: unknown) => {
      // Stale-chunk dışı hatalarda (örn. modülün kendi içindeki syntax/runtime
      // hatası) reload bir şey çözmez → doğrudan ErrorBoundary'e bırak.
      if (!isLikelyStaleChunkError(error)) throw error;

      const now = Date.now();
      const last = Number(sessionStorage.getItem(RELOAD_TS_KEY) ?? 0);
      if (now - last > RELOAD_COOLDOWN_MS) {
        sessionStorage.setItem(RELOAD_TS_KEY, String(now));
        log.warn('stale chunk; sayfa otomatik yenileniyor', { error: String(error) });
        window.location.reload();
        // Reload başlayana kadar React'i Suspense'te beklet (asla resolve etmez).
        return new Promise<{ default: T }>(() => {});
      }

      // Cooldown içinde 2. kez → reload çözmedi, gerçek hata. ErrorBoundary göster.
      log.error('chunk yüklenemedi (reload sonrası da)', error instanceof Error ? error : undefined);
      throw error;
    }),
  );
}
